import Badge from "components/Badge";
import React from "react";
import LoadingIcon from "components/LoadingIcon";
import { IconDeviceFloppy, IconSparkles } from "@tabler/icons-react";
import clsx from "clsx";
import { XIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import { ObjectProductMatch } from "api-client";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import Tooltip from "components/Tooltip";
import { useTranslation } from "react-i18next";
import useMappingEditor from "../useMappingEditor";

interface DMEMappingChipProps {
  product: ObjectProductMatch;
  key?: number;
  unmatch?: () => Promise<void>;
  loading?: boolean;
  className?: string;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

export default observer(function DMEMappingChip({
  product,
  key,
  unmatch,
  loading,
  className,
  setLoading,
}: Readonly<DMEMappingChipProps>) {
  const { t } = useTranslation();
  const { getMappingChipTooltipContent, getMappingChipIcon } =
    useMappingEditor();

  const isCompliant = product.compliance === "OK";
  const isOutdated = product.product_outdated ?? false;

  const showProductDetails = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    MEStore.setOpenProductDetailsPopup({
      open: true,
      id: product.id,
      name: product.name,
      type: product.product_type,
    });
  };

  const getChipType = () => {
    if (isOutdated) {
      return "attention";
    }
    if (isCompliant) {
      return "success";
    }
    return "warning";
  };

  function badgeContent() {
    return (
      <Tooltip
        content={getMappingChipTooltipContent(isCompliant, isOutdated)}
        tooltipClassName="bg-white shadow-lg !p-4 rounded-md min-w-[700px]"
      >
        <Badge
          key={key}
          type={getChipType()}
          className="text-xs text-gray-700 !px-2 py-0"
        >
          <div className="flex items-center overflow-hidden min-h-full">
            <div className="pr-1">
              {getMappingChipIcon(
                isCompliant,
                product.product_type,
                isOutdated
              )}
            </div>
            <div className={clsx("truncate cursor-pointer", className)}>
              {product.name ?? "-"}
            </div>
          </div>

          {!loading && unmatch ? (
            <XIcon
              width={15}
              className={clsx("ml-2 cursor-pointer min-w-[15px] max-w-[15px]", {
                "text-yellow-700 hover:text-yellow-500": isOutdated,
                "text-green-700 hover:text-green-500":
                  !isOutdated && isCompliant,
                "text-red-700 hover:text-red-500": !isOutdated && !isCompliant,
              })}
              onClick={async (e) => {
                e.stopPropagation();
                setLoading?.(true);
                await unmatch?.();
                setLoading?.(false);
              }}
            />
          ) : (
            loading &&
            unmatch && (
              <LoadingIcon
                className={clsx("ml-2", {
                  "!text-yellow-500": isOutdated,
                  "!text-green-500": !isOutdated && isCompliant,
                  "!text-red-500": !isOutdated && !isCompliant,
                })}
              />
            )
          )}
        </Badge>
      </Tooltip>
    );
  }

  return (
    <div
      className="flex justify-start max-w-[80%] items-center"
      onClick={showProductDetails}
    >
      {isOutdated ? (
        <Tooltip content={t("mappingEditor.outDatedProductTooltip")}>
          {badgeContent()}
        </Tooltip>
      ) : (
        badgeContent()
      )}
      {product.match_type === "auto" ? (
        <div className="px-1">
          <IconSparkles className="text-gray-400 w-5" />
        </div>
      ) : null}
      {product.match_type === "pre" ? (
        <div className="px-1">
          <IconDeviceFloppy className="text-gray-400 w-5" />
        </div>
      ) : null}
    </div>
  );
});
